import React, { useContext, useEffect, useState, memo, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  makeStyles,
  withStyles,
  DialogContent,
  Dialog,
} from '@material-ui/core';
import { Button, Typography } from '@redshelf/redshelf-ui';

import { SCREEN_SIZE_CATEGORIES, STATIC_PATH } from 'constants';
import {
  BookValuesContext,
  UserContentItemModifiersContext,
  UserSessionValuesContext,
  ViewportValuesContext,
} from 'contexts';
import { getLocalSettingsProxy } from 'utilities';

const TITLE = 'Are you still there?';
const MESSAGE = `It looks like you've been idle for a little bit. Are you still around? If so, please click the "I'm Still Here" button below. If not, you will be automatically logged out in `;
const BUTTON_LABEL = "I'm Still Here";

const DEFAULT_BACK_TO_URL = 'https://redshelf.com/accounts/login/';

const MILLISECONDS_IN_SECOND = 1000;
const MILLISECOND_IN_MINUTE = 60 * MILLISECONDS_IN_SECOND;
const DEFAULT_KICKOUT_TIME = 15; // Countdown to 15 minutes

const useStyles = makeStyles((theme) => ({
  paper: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    maxWidth: theme.spacing(145.5),
    padding: theme.spacing(7.5, 6.5, 6.5, 6.5),

    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(0, 2, 1, 2),
    },
  },
  contentWrapper: {
    color: theme.palette.greyBlue[700],
    maxWidth: theme.spacing(74),
    cursor: 'default',
  },
  title: {
    padding: theme.spacing(0, 1, 2, 1),

    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(2.3125, 1),
    },
  },
  stillHereButton: {
    marginTop: theme.spacing(4.5),
    alignSelf: 'flex-start',

    [theme.breakpoints.down('xs')]: {
      marginTop: theme.spacing(3),
      alignSelf: 'flex-end',
    },
  },
  owliverImg: {
    alignSelf: 'flex-end',
    marginBottom: theme.spacing(-6.5),
  },
}));

const StyledDialogContent = withStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(0, 1, 1, 1),
    '&:first-child': {
      padding: 0,
    },
  },
}))(DialogContent);

// Timer for countdown before current session is ended.
// Uses localStorage to synchronize the expiry time across all tabs
const Timer = memo(({ onTimerEnd, time, localSettings }) => {
  const [countTime, setCountTime] = useState(null);

  // Initialize or read the expiry timestamp from localStorage
  useEffect(() => {
    const expiresAt = localSettings.sessionTracking.inactivityDialogExpiresAt;

    if (!expiresAt) {
      // Set expiry time if not already set
      const newExpiresAt = Date.now() + time * MILLISECOND_IN_MINUTE;
      localSettings.sessionTracking.inactivityDialogExpiresAt = newExpiresAt;
      setCountTime(time * MILLISECOND_IN_MINUTE);
    } else {
      // Calculate remaining time based on shared expiry timestamp
      const remaining = expiresAt - Date.now();
      setCountTime(Math.max(0, remaining));
    }
  }, [time, localSettings]);

  useEffect(() => {
    if (countTime === null) {
      // Timer not initialized yet
      return;
    }

    if (countTime <= 0) {
      onTimerEnd();
      return;
    }

    const timer = setTimeout(() => {
      const expiresAt = localSettings.sessionTracking.inactivityDialogExpiresAt;
      if (!expiresAt) {
        return;
      }

      const remaining = expiresAt - Date.now();
      setCountTime(Math.max(0, remaining));
    }, MILLISECONDS_IN_SECOND);

    return () => clearTimeout(timer);
  }, [countTime, onTimerEnd, localSettings]);

  const countTimeFormat =
    countTime === null || countTime > MILLISECOND_IN_MINUTE ? (
      <>
        <span className="noTranslate" data-chromatic="ignore">
          {countTime === null
            ? '...'
            : Math.ceil(countTime / MILLISECOND_IN_MINUTE)}
        </span>{' '}
        minutes.
      </>
    ) : (
      <>
        <span className="noTranslate" data-chromatic="ignore">
          {Math.ceil(countTime / MILLISECONDS_IN_SECOND)}
        </span>{' '}
        seconds.
      </>
    );

  return countTimeFormat;
});

const InactivityDialog = ({
  dialogAction = () => {},
  onKickOut = () => {},
  open = false,
  timeBeforeKickOut = DEFAULT_KICKOUT_TIME,
  ...props
}) => {
  const classes = useStyles();

  const { book } = useContext(BookValuesContext);

  const { backToShelfInfo } = useContext(UserSessionValuesContext);

  const { onDialogClose, onDialogOpen } = useContext(
    UserContentItemModifiersContext,
  );

  const { viewport } = useContext(ViewportValuesContext);
  const isMobileSize = viewport.widthCategory <= SCREEN_SIZE_CATEGORIES.PHONE;

  const id = 'inactivityDialog';
  const [isOpen, setIsOpen] = useState(false);
  const [localSettings] = useState(getLocalSettingsProxy);
  const [logoutAttempted, setLogoutAttempted] = useState(false);
  const logoutAttemptedRef = useRef(false);

  const triggerDialogAction = () => {
    // Clear the expiry timestamp and close dialog in all tabs
    localSettings.sessionTracking.inactivityDialogExpiresAt = null;
    localSettings.sessionTracking.inactivityDialogOpen = false;

    dialogAction();
    setIsOpen(false);
    onDialogClose(id);
  };

  // Sync dialog open state when the prop changes
  useEffect(() => {
    if (open) {
      localSettings.sessionTracking.inactivityDialogOpen = true;
      setIsOpen(true);
    }
  }, [open, localSettings]);

  // Listen for storage events to sync dialog state across tabs
  useEffect(() => {
    const handleStorageChange = (e) => {
      // Check if logout was triggered in another tab
      if (
        e.key &&
        e.key.includes('sessionTracking.shouldLogout') &&
        e.newValue === 'true'
      ) {
        // Another tab initiated logout, do the same
        if (!logoutAttempted) {
          setLogoutAttempted(true);
          onKickOut();

          // Clear the flag before redirect to prevent infinite loop
          localSettings.sessionTracking.shouldLogout = false;

          // Check if we have a whitelabel backToUrl
          const hasBackToUrl =
            backToShelfInfo?.backToUrl &&
            backToShelfInfo.backToUrl.trim() !== '';

          // If no backToUrl, redirect directly to RedShelf login
          if (!hasBackToUrl) {
            window.location.href = DEFAULT_BACK_TO_URL;
            return;
          }

          // Otherwise reload - StatusPage will show the backToUrl button
          window.location.reload();
        }
        return;
      }

      // Check if the inactivity dialog was closed in another tab
      if (
        e.key &&
        e.key.includes('sessionTracking.inactivityDialogOpen') &&
        e.newValue === 'false'
      ) {
        setIsOpen(false);
        onDialogClose(id);
      }

      // Check if the inactivity dialog was opened in another tab
      if (
        e.key &&
        e.key.includes('sessionTracking.inactivityDialogOpen') &&
        e.newValue === 'true'
      ) {
        setIsOpen(true);
      }

      // Check if the expiry timestamp was cleared (dialog closed)
      if (
        e.key &&
        e.key.includes('sessionTracking.inactivityDialogExpiresAt') &&
        !e.newValue
      ) {
        setIsOpen(false);
        onDialogClose(id);
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [
    onDialogClose,
    logoutAttempted,
    onKickOut,
    localSettings,
    backToShelfInfo,
  ]);

  const onTimerEnd = async () => {
    // Prevent multiple logout attempts using ref (more reliable than state)
    if (logoutAttemptedRef.current) {
      return; // Don't do anything, let the first call finish
    }

    logoutAttemptedRef.current = true;
    setLogoutAttempted(true);

    // Check if we have a whitelabel backToUrl FIRST
    const backToUrl = backToShelfInfo?.backToUrl;
    const hasBackToUrl = Boolean(backToUrl && backToUrl.trim() !== '');

    // Log out
    onKickOut();

    // Signal all tabs to logout
    localSettings.sessionTracking.shouldLogout = true;
    localSettings.sessionTracking.inactivityDialogExpiresAt = null;
    localSettings.sessionTracking.inactivityDialogOpen = false;

    // Send logout request (don't wait for it to complete)
    fetch(`/logout-with-reason/?reason=inactivity&epub_id=${book.pk}`).catch(
      (error) => console.error('Inactivity logout request failed:', error),
    );

    // Small delay to let the fetch start
    await new Promise((resolve) => setTimeout(resolve, 100));

    // If no backToUrl, redirect directly to RedShelf login
    if (!hasBackToUrl) {
      window.location.href = DEFAULT_BACK_TO_URL;
      return;
    }

    // Otherwise reload - StatusPage will show the backToUrl button
    window.location.reload();
  };

  const handleClose = (event, reason) => {
    // MUI bug:
    // "The prop `disableBackdropClick` of `ForwardRef(Modal)` is deprecated.
    // Use the onClose prop with the `reason` argument to filter the `backdropClick` events."
    if (reason === 'backdropClick') {
      return false;
    }
    onDialogClose(id);
  };

  return (
    <Dialog
      aria-label={TITLE}
      aria-labelledby="inactivity-title"
      className={classes.root}
      disableEscapeKeyDown
      fullWidth={true}
      id={id}
      maxWidth="lg"
      open={isOpen}
      onClose={handleClose}
      PaperProps={{ className: classes.paper }}
      TransitionProps={{ onEntered: () => onDialogOpen(id) }}
      {...props}
    >
      <div className={classes.contentWrapper}>
        <Typography
          className={classes.title}
          id="inactivity-title"
          variant="h3"
        >
          {TITLE}
        </Typography>

        <StyledDialogContent>
          <Typography variant="body1">
            {MESSAGE}
            <Timer
              onTimerEnd={onTimerEnd}
              time={timeBeforeKickOut}
              localSettings={localSettings}
            />
          </Typography>

          <Button
            className={classes.stillHereButton}
            onClick={triggerDialogAction}
          >
            {BUTTON_LABEL}
          </Button>
        </StyledDialogContent>
      </div>

      {!isMobileSize && (
        <img
          alt=""
          className={classes.owliverImg}
          src={`${STATIC_PATH}/images/owl.svg`}
        />
      )}
    </Dialog>
  );
};

InactivityDialog.propTypes = {
  dialogAction: PropTypes.func, // Function to call on button click of the dialog
  onKickOut: PropTypes.func, // Function when the kickoutTime is reached
  open: PropTypes.bool, // boolean to open/close dialog
  timeBeforeKickOut: PropTypes.number, // time in minutes before ending user session
};

export default InactivityDialog;
